import {
  arrayOf, func, number, oneOf, oneOfType, shape, string
} from 'prop-types';
import { noop } from 'lodash-es';
import React from 'react';
import {
  questionTypeValues,
  schemaPartItemTypeValues,
  SurveySchemaPartItemEditorType
} from '../../../../constants/types';
import StaticText from '../../../StaticText';
import LikertQuestion from '../../../LikertQuestion';
import FreeQuestion from '../../../FreeQuestion';
import SelectionQuestion from '../../../SelectionQuestion';
import DropDownQuestion from '../../../DropDownQuestion';
import LikertOptionsHeading from '../../../LikertOptionsHeading';

const SchemaPartItemPreview = ({
  item, lang, questionOrder, answers, translate
}) => {
  const getQuestionAnswer = (question) => {
    if (!answers) {
      return null;
    }

    const { answer } = Array.isArray(answers)
      ? answers.find((a) => a.questionId === question.id)
      : answers;

    if ([questionTypeValues.CHECKBOX_GROUP,
      questionTypeValues.TEXT_FIELD].includes(question.type)) {
      return answer;
    }
    return answer && answer.length ? answer[0] : null;
  };

  const getQuestionPresentation = (question, responseOptions = []) => {
    const questionId = question.id || questionOrder[question.code];
    const questionWithId = { ...question, id: questionId };
    const responseOptionsWithIds = responseOptions.map((responseOption, idx) => {
      const optionId = responseOption.id || idx;
      return { ...responseOption, id: optionId };
    });
    const questionAndResponseOptionsWithIds = {
      ...questionWithId,
      responseOptions: responseOptionsWithIds
    };
    const answer = getQuestionAnswer(question);

    switch (question.type) {
      case questionTypeValues.LIKERT:
        return (
          <LikertQuestion
            key={questionId}
            orderNum={questionOrder[question.code]}
            question={questionAndResponseOptionsWithIds}
            answer={answer}
            onChange={noop}
            lang={lang}
            responseOptions={responseOptionsWithIds}
            translate={translate}
          />
        );
      case questionTypeValues.TEXT_FIELD:
        return (
          <FreeQuestion
            orderNum={questionOrder[question.code]}
            question={questionWithId}
            answer={answer}
            onChange={noop}
            selectedLang={lang}
            translate={translate}
          />
        );
      case questionTypeValues.RADIO_BUTTON_GROUP:
        return (
          <SelectionQuestion
            key={questionId}
            question={questionAndResponseOptionsWithIds}
            orderNum={questionOrder[question.code]}
            responseOptions={responseOptionsWithIds}
            answer={answer}
            onChange={noop}
            translate={translate}
            lang={lang}
          />
        );
      case questionTypeValues.CHECKBOX_GROUP:
        return (
          <SelectionQuestion
            key={questionId}
            question={questionAndResponseOptionsWithIds}
            orderNum={questionOrder[question.code]}
            responseOptions={responseOptionsWithIds}
            answer={answer}
            onChange={noop}
            translate={translate}
            lang={lang}
          />
        );
      case questionTypeValues.DROPDOWN:
        return (
          <DropDownQuestion
            key={questionId}
            question={questionAndResponseOptionsWithIds}
            orderNum={questionOrder[question.code]}
            responseOptions={responseOptionsWithIds}
            answer={answer}
            onChange={noop}
            translate={translate}
            lang={lang}
          />
        );
      default:
        return null;
    }
  };

  const getQuestionGroupPresentation = (questionGroup) => {
    const responseOptions = questionGroup.responseOptions.map((responseOption, idx) => {
      const id = responseOption.id || idx;
      return { ...responseOption, id };
    });
    const mainOptionsHeading = (
      <LikertOptionsHeading key="optionsHeading" responseOptions={responseOptions} lang={lang} />
    );

    return [mainOptionsHeading, ...questionGroup.questions.map((question) =>
      getQuestionPresentation(
        question,
        responseOptions
      ))];
  };

  const getSchemaPartItemPresentation = () => {
    switch (item.type) {
      case schemaPartItemTypeValues.QUESTION:
        return getQuestionPresentation(item.question, item.question.responseOptions);
      case schemaPartItemTypeValues.QUESTION_GROUP:
        return getQuestionGroupPresentation(item.questionGroup);
      case schemaPartItemTypeValues.STATIC_TEXT:
        return <StaticText staticText={item.staticText} lang={lang} />;
      default:
        return null;
    }
  };

  return (
    <div>{getSchemaPartItemPresentation(item)}</div>
  );
};

SchemaPartItemPreview.propTypes = {
  item: SurveySchemaPartItemEditorType.isRequired,
  lang: string.isRequired,
  answers: oneOfType([
    shape({
      questionId: number,
      answers: oneOf([arrayOf(number), string])
    }),
    arrayOf(shape({
      questionId: number,
      answers: arrayOf(number)
    }))
  ]),
  questionOrder: shape().isRequired,
  translate: func.isRequired
};

SchemaPartItemPreview.defaultProps = {
  answers: undefined
};

export default SchemaPartItemPreview;
